"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, Apple, Brain, GraduationCap, Dog, Sparkles, Clock, Scissors, Smile, ShieldAlert, ShoppingBag, FileHeart, ArrowRight, Stethoscope, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
interface TopicItem {
  id: number;
  title: string;
  description: string;
  to: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  tag: string;
}
const topicsData: TopicItem[] = [{
  id: 1,
  title: "Dog health",
  description: "Symptom guides, preventive wellness schedules, and veterinary-reviewed care advice.",
  to: "/PetHealthPage",
  icon: Activity,
  tag: "Clinical Care"
}, {
  id: 2,
  title: "Dog nutrition",
  description: "Evidence-based feeding plans, ingredient analyses, and life-stage dietary requirements.",
  to: "/NutritionPage",
  icon: Apple,
  tag: "Dietary"
}, {
  id: 3,
  title: "Dog behavior",
  description: "Understanding canine psychology, body language cues, and anxiety management.",
  to: "/BehaviorTrainingPage",
  icon: Brain,
  tag: "Psychology"
}, {
  id: 4,
  title: "Dog training",
  description: "Positive reinforcement techniques, obedience steps, and socialization routines.",
  to: "/BehaviorTrainingPage",
  icon: GraduationCap,
  tag: "Routines"
}, {
  id: 5,
  title: "Dog breeds",
  description: "Comprehensive profiles on temperament, genetic health tendencies, and exercise needs.",
  to: "/BreedsPage",
  icon: Dog,
  tag: "Directory"
}, {
  id: 6,
  title: "Puppies",
  description: "Early development milestones, initial vaccination timelines, and house-training essentials.",
  to: "/DogsPage",
  icon: Sparkles,
  tag: "Early Care"
}, {
  id: 7,
  title: "Senior dogs",
  description: "Joint support protocols, cognitive care adjustments, and comfort management for aging dogs.",
  to: "/DogsPage",
  icon: Clock,
  tag: "Geriatric"
}, {
  id: 8,
  title: "Grooming",
  description: "Coat maintenance practices, ear hygiene, nail trims, and skin barrier protection.",
  to: "/DogsPage",
  icon: Scissors,
  tag: "Hygiene"
}, {
  id: 9,
  title: "Dental care",
  description: "Periodontal disease prevention, daily cleaning guidance, and veterinary scaling insights.",
  to: "/PetHealthPage",
  icon: Smile,
  tag: "Oral Health"
}, {
  id: 10,
  title: "Dog safety",
  description: "Household hazard checklists, emergency toxicity guides, and seasonal safety precautions.",
  to: "/DogsPage",
  icon: ShieldAlert,
  tag: "Protocols"
}, {
  id: 11,
  title: "Dog products",
  description: "Unbiased evaluations of harnesses, interactive gear, orthopedic beds, and essentials.",
  to: "/ProductsReviewsPage",
  icon: ShoppingBag,
  tag: "Gear Review"
}, {
  id: 12,
  title: "Dog insurance",
  description: "Transparent breakdowns of accident and illness policies, deductibles, and coverage limits.",
  to: "/DogsPage",
  icon: FileHeart,
  tag: "Financial"
}];
export default function IntroTopics() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="intro-topics" data-nav="light" className="bg-background text-foreground py-12 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Hub Header & Lead */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            <Stethoscope className="size-3.5 text-foreground" />
            <span>Canine Knowledge Base</span>
          </div>
          
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground leading-tight mb-4">
            Dog care &amp; wellness
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed mb-6">
            Everything you need to help your dog live a healthier, happier life.
          </p>

          <div className="flex flex-wrap items-center gap-6 pt-2 border-t border-border text-xs sm:text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-foreground" />
              <span>Veterinary-reviewed clinical guidance</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-foreground" />
              <span>12 comprehensive canine disciplines</span>
            </div>
          </div>
        </div>

        {/* 12-Card Structured Topic Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
          {topicsData.map((topic, i) => {
          const Icon = topic.icon;
          return <motion.div key={topic.id} data-index={i} variants={itemVariants} className="h-full">
                <Card className="h-full flex flex-col justify-between bg-card text-card-foreground border border-border rounded-xl p-5 hover:border-foreground/30 hover:shadow-sm transition-[border-color,box-shadow] duration-200">
                  <div>
                    <CardHeader className="p-0 mb-4">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="p-2.5 rounded-lg bg-muted text-foreground flex items-center justify-center">
                          <Icon className="size-5" />
                        </div>
                        <span className="text-[11px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded bg-background border border-border text-muted-foreground">
                          {topic.tag}
                        </span>
                      </div>
                      <CardTitle className="font-serif text-xl font-semibold text-foreground tracking-tight">
                        {topic.title}
                      </CardTitle>
                    </CardHeader>
                    
                    <CardContent className="p-0 mb-6">
                      <CardDescription className="text-muted-foreground text-sm font-default leading-relaxed">
                        {topic.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <CardFooter className="p-0 pt-4 border-t border-border/70 flex items-center justify-between">
                    <Button asChild variant="ghost" size="sm" className="px-0 h-auto font-medium text-foreground hover:text-foreground hover:bg-transparent group">
                      <Link to={topic.to} className="inline-flex items-center gap-1.5 text-xs sm:text-sm">
                        <span>Explore topic</span>
                        <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}